import React, { useState } from 'react';
import SimpleConfetti from '../components/SimpleConfetti';

// Data extracted from QUIZ-AND-ON-BOARDING.md
const quizData = [
  {
    question: 'What is the primary mission of Play2Visit?',
    options: [
      'To create the most entertaining mobile games on the market.',
      'To interrupt customers with more effective digital ads.',
      'To help brands stop renting attention and start owning engagement.',
      'To replace e-commerce with in-store sales.',
    ],
    correctAnswer: 'To help brands stop renting attention and start owning engagement.',
  },
  {
    question: 'The term "Phygital" refers to:',
    options: [
      'A new type of digital-only game.',
      'A seamless bridge between a brand\'s physical and digital presence.',
      'A strategy focused only on physical, in-store marketing.',
      'A method for analyzing a company\'s financial and digital assets.',
    ],
    correctAnswer: 'A seamless bridge between a brand\'s physical and digital presence.',
  },
  {
    question: 'What is the single most important metric that Play2Visit helps clients measure?',
    options: [
      'Click-Through Rate (CTR)',
      'App Downloads',
      'Player-to-Visitor Rate',
      'Social Media Impressions',
    ],
    correctAnswer: 'Player-to-Visitor Rate',
  },
  {
    question: 'Which of the following is a key client "pain point" that Play2Visit is designed to solve?',
    options: [
      '"Our website loading speed is too slow."',
      '"We can\'t prove our social media spend leads to in-store sales."',
      '"Our email open rates have declined by 5%."',
      '"We need a new logo and brand guidelines."',
    ],
    correctAnswer: '"We can\'t prove our social media spend leads to in-store sales."',
  },
  {
    question: 'What is the recurring commission rate for a "Certified Partner"?',
    options: [
      '15% on the first-year contract.',
      '25% on the first-year contract.',
      '25% recurring commission on the lifetime value of the client.',
      '35% recurring commission on the lifetime value of the client.',
    ],
    correctAnswer: '25% recurring commission on the lifetime value of the client.',
  },
  {
    question: 'How is the Play2Visit platform technically integrated into a client\'s app or website?',
    options: [
      'It requires a full custom rebuild of the client\'s app.',
      'Through a simple web embed code.',
      'It is only available as a standalone, separate application.',
      'It requires the client to switch their hosting provider to Play2Visit.',
    ],
    correctAnswer: 'Through a simple web embed code.',
  },
  {
    question: '"Earned Ad Moments" are designed to:',
    options: [
      'Interrupt the game at key moments to maximize ad revenue.',
      'Make ads a voluntary, player-chosen part of the game experience.',
      'Replace in-game rewards with video advertisements.',
      'Run ads before the game is allowed to start.',
    ],
    correctAnswer: 'Make ads a voluntary, player-chosen part of the game experience.',
  },
  {
    question: 'If a client says, "This seems too complicated to set up," what is the best response?',
    options: [
      '"It is complicated, but it\'s worth it."',
      '"It\'s actually a simple embed code, and our team provides full support, making it far more cost-effective than building a custom game from scratch."',
      '"We can offer a discount to compensate for the complexity."',
      '"You will need to hire a team of developers to manage the integration."',
    ],
    correctAnswer: '"It\'s actually a simple embed code, and our team provides full support, making it far more cost-effective than building a custom game from scratch."',
  },
  {
    question: 'What is the minimum Player-to-Visitor conversion rate a partner must maintain to achieve "Certified Partner" status?',
    options: [
      '1%',
      '5%',
      '10%',
      '25%',
    ],
    correctAnswer: '5%',
  },
  {
    question: 'What is the primary purpose of the "In-Store Scoreboards & Rewards"?',
    options: [
      'To provide a powerful, tangible incentive for players to make a physical visit.',
      'To show a list of all players, regardless of their location.',
      'To allow players to purchase in-game items.',
      'To display the partner agency\'s contact information.',
    ],
    correctAnswer: 'To provide a powerful, tangible incentive for players to make a physical visit.',
  },
];

const OnboardingForm = () => {
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // null, 'success', or an error message string

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setSubmissionStatus('You must agree to the Terms & Conditions to proceed.');
      return;
    }

    setIsSubmitting(true);
    setSubmissionStatus(null);

    const form = e.target;
    const formData = {
      fullName: form.fullName.value,
      companyName: form.companyName.value,
      companyWebsite: form.companyWebsite.value,
      linkedinUrl: form.linkedinUrl.value,
      phoneNumber: form.phoneNumber.value,
      workEmail: form.workEmail.value,
    };

    try {
      const response = await fetch('https://codespace.fi/fusionx/partnerform.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmissionStatus('success');
      } else {
        throw new Error(result.error || 'An unknown error occurred.');
      }
    } catch (error) {
      setSubmissionStatus(error.message || 'A network error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submissionStatus === 'success') {
    return (
      <div className="w-full max-w-2xl mx-auto mt-8 text-center bg-gray-800/60 border border-green-500/50 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-green-300 mb-4">Registration Successful!</h2>
        <p className="text-gray-200">Welcome aboard! Please check your email for your sales toolkit and the next steps. We're excited to have you.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto mt-8">
      <h2 className="text-2xl font-bold text-white mb-4">Complete Your Registration</h2>
      <form onSubmit={handleSubmit} className="bg-gray-800/60 border border-white/10 rounded-2xl p-8 space-y-6">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
          <input type="text" id="fullName" name="fullName" required disabled={isSubmitting} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50" />
        </div>
        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
          <input type="text" id="companyName" name="companyName" required disabled={isSubmitting} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50" />
        </div>
        <div>
          <label htmlFor="companyWebsite" className="block text-sm font-medium text-gray-300 mb-2">Company Website</label>
          <input type="url" id="companyWebsite" name="companyWebsite" required disabled={isSubmitting} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50" />
        </div>
        <div>
          <label htmlFor="linkedinUrl" className="block text-sm font-medium text-gray-300 mb-2">LinkedIn Profile URL</label>
          <input type="url" id="linkedinUrl" name="linkedinUrl" required disabled={isSubmitting} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50" />
        </div>
        <div>
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-300 mb-2">Phone Number</label>
          <input type="tel" id="phoneNumber" name="phoneNumber" required disabled={isSubmitting} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50" />
        </div>
        <div>
          <label htmlFor="workEmail" className="block text-sm font-medium text-gray-300 mb-2">Work Email</label>
          <input type="email" id="workEmail" name="workEmail" required disabled={isSubmitting} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50" />
        </div>
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input id="terms" name="terms" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={isSubmitting} className="focus:ring-purple-500 h-4 w-4 text-purple-600 border-gray-600 rounded" />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms" className="text-gray-300">
              I have read and agree to the{' '}
              <a href="/PARTNER-TERMS.md" target="_blank" rel="noopener noreferrer" className="font-medium text-purple-400 hover:underline">
                Play2Visit Partner Program Terms & Conditions
              </a>
              .
            </label>
          </div>
        </div>
        
        {submissionStatus && submissionStatus !== 'success' && (
          <div className="bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
            <p><span className="font-bold">Error:</span> {submissionStatus}</p>
          </div>
        )}

        <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-lg disabled:opacity-70 disabled:cursor-not-allowed">
          {isSubmitting ? 'Activating...' : 'Activate Partnership'}
        </button>
      </form>
    </div>
  );
};


const Quiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleNextQuestion = () => {
    if (selectedOption === quizData[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
    setSelectedOption('');
    if (currentQuestion < quizData.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  if (showResult) {
    const isPerfectScore = score === quizData.length;
    return (
      <div className="bg-gray-950 text-white p-8 min-h-screen flex items-center justify-center">
        {isPerfectScore && <SimpleConfetti />}
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h1 className="text-3xl md:text-4xl font-bold">Quiz Complete!</h1>
          <p className="text-4xl font-bold">Your Score: <span className={isPerfectScore ? 'text-green-400' : 'text-yellow-400'}>{score} / {quizData.length}</span></p>
          {isPerfectScore ? (
            <>
              <h2 className="text-2xl font-bold text-green-300">Congratulations, You Passed! You are now eligible to become an Authorized Reseller.</h2>
              <button
                onClick={() => setShowOnboarding(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-lg"
              >
                Claim Your Partner Badge & Sales Toolkit
              </button>
            </>
          ) : (
            <>
              <p className="text-lg text-yellow-300">A perfect score is required to proceed. Please review the training materials and try again.</p>
              <button onClick={() => window.location.reload()} className="border border-white/30 hover:border-white px-8 py-4 rounded-full font-semibold text-gray-100 text-lg">
                Try Again
              </button>
            </>
          )}
          {showOnboarding && <OnboardingForm />}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-950 text-white p-8 min-h-screen flex items-center justify-center">
      <div className="w-full max-w-2xl">
        <div className="bg-gray-900 border border-white/10 rounded-2xl p-8 space-y-8">
          <div>
            <div className="flex justify-between items-center text-sm uppercase tracking-widest text-purple-300 mb-4">
              <span>Question {currentQuestion + 1} / {quizData.length}</span>
              <span>Score: {score}</span>
            </div>
            <p className="text-xl font-semibold text-white">{quizData[currentQuestion].question}</p>
          </div>

          <div className="space-y-4">
            {quizData[currentQuestion].options.map((option, index) => (
              <label key={index} htmlFor={`option-${index}`} className="block p-4 rounded-lg border border-gray-700 bg-gray-800/50 hover:bg-gray-700/50 has-[:checked]:bg-purple-600/30 has-[:checked]:border-purple-500 transition-colors">
                <input
                  type="radio"
                  id={`option-${index}`}
                  name="quizOption"
                  value={option}
                  checked={selectedOption === option}
                  onChange={handleOptionChange}
                  className="hidden"
                />
                <span className="text-gray-200">{option}</span>
              </label>
            ))}
          </div>
          
          <button
            onClick={handleNextQuestion}
            disabled={!selectedOption}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentQuestion < quizData.length - 1 ? 'Next Question' : 'Finish Quiz'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Quiz;